<template>
    <div class="container">
        <h1>Comment déléguer ses ADAs depuis son Ledger Nano S ou X (avec Yoroi)</h1>
        <p class="intro">
            Yoroi est un portefeuille HOT ou COLD (Trezor, Ledger Nano S ou X) qui vous permet de déleguer vos ADAs à une Stake Pool
            pour gagner des intérêts (4 à 8% par an). Tutoriel de A à Z.
        </p>

        <h2>Hot Staking ou Cold Staking.</h2>
        <p>
            Yoroi est un portefeuille intégré à votre navigateur (plugin) qui vous permettra de garder et dépenser vos ADAs à volonté, tout
            en vous laissant les déposer sur un compte de <a href="/doc-cardano/qu-est-ce-que-le-staking">staking</a>.
            Sur ce portefeuille, vous pouvez créer des comptes <b>HOT</b> et <b>COLD</b> - c'est à dire dont la clé privée (qui donne le pouvoir de
            valider les transactions sortantes) est hébergée sur un disque de votre machine dans le premier cas, et sur votre portefeuille COLD dans le second - et
            déleguer vos ADAs à une Stake Pool depuis ces comptes, pour y gagner des intérêts réguliers (entre 4% et 8% par an, généralement).
        </p>

        <p>
            Si le <b>HOT staking</b> - staking depuis un portefeuille HOT - peut se faire dans des conditions <i>relativement</i> sécurisées dans
            certain cas (voir par exemple les techniques de
            <a href="/tutoriel/comment-staker-ses-ADAs-avec-Daedalus">notre tutoriel sur Daedalus</a>), le <u>Saint Graal du staking reste le COLD
                staking</u>. Il est <b>conseillé pour quiconque possède des quantités importantes de cryptomonnaies d'investir entre 50€ et 120€ pour
                acquérir un portefeuille COLD sécurisé</b>: un dispositif physique (fonctionnant par USB ou Bluetooth) qui se connectera à votre PC et
            qui échangera
            des informations avec. Votre clé privée ne sera jamais, ni sur votre machine, ni sur Internet. Vous pourrez alors <b>déleguer vos ADAs
                depuis votre portefeuille COLD</b>.
        </p>

        <p>
            Si vous songez à acquérir un portefeuille COLD, songez à consulter notre article sur <a href="/doc-cardano/les-portefeuilles-compatibles-avec-le-staking-d-ADA">
                les portefeuilles compatibles avec ADA</a>.
        </p>

        <p>Cet article concerne le <b>Ledger Nano S ou X</b> avant tout, mais si vous désirez déleguer depuis votre <b>Trezor Wallet</b>, ou depuis un
            compte HOT, n'hésitez pas à suivre ce tutoriel, c'est très similaire.</p>

        <h2>Préparez votre Ledger Nano</h2>

        <p>
            Pour ce tutoriel, il vous faut:
        </p>
        <ul>
            <li>Votre Ledger Nano (X ou S) avec un firmware à jour et <b>l'application ADA installée</b> et sa connectique</li>
            <li>Ledger Live installé et à jour</li>
        </ul>

        <h2>Installation de Yoroi</h2>
        <p>
            Installez l'extension Yoroi pour Chrome, Brave, Firefox ou l'application mobile Yoroi depuis <a href="https://yoroi-wallet.com/#/about">
                le site officiel de Yoroi</a>. Vérifiez toujours dans la barre d'URL que vous êtes bien sur yoroi-wallet.com et que la connexion est sécurisée
            (HTTPS).
        </p>

        <img v-lazy-load data-src="/img/tuto-ledger-yoroi/yoroi-connected.webp" alt="Yoroi connecté" />

        <p>
            Une fois Yoroi installé et lancé, nous pouvons passer à la suite.
        </p>

        <h2>Créez un portefeuille connecté à votre Ledger sur Yoroi</h2>
        <p>
            Nous allons créer sur Yoroi un portefeuille connecté à notre Ledger. Prenons ici 1 minute pour <i>bien</i> comprendre ce que ça veut dire.
            Notre <b>clé privée</b> restera et ne quittera jamais notre Ledger: <b>celle-ci sera requis pour toutes les transactions sortantes
                (dépenses)</b>.
            En revanche, la <b>clé publique</b>, elle, sera communiquée à Yoroi, ce qui lui permettra de connaître nos adresses, et de vérifier
            l'authenticité de nos transactions (vérifier que ça vient bien de NOTRE ledger).
        </p>

        <p>
            Ouvrez l'extension Yoroi sur votre navigateur (ou votre mobile) et choisissez la langue (français, je suppose ?). Validez les termes
            et conditions.
        </p>

        <p>
            Yoroi va vous demander si vous préferez l'interface <b>simple</b> ou <b>avancée</b>. Choisissez ce que vous préferez, en fonction de
            votre niveau de compréhension des technologies blockchain. Validez, et autorisez Yoroi à utiliser les <b>urls de paiement Cardano</b>.
        </p>

        <p>
            Vient alors le moment de la création de notre portefeuille. Cliquez sur le bouton "Ajouter un nouveau wallet" en haut à droite.
        </p>

        <img v-lazy-load data-src="/img/tuto-ledger-yoroi/yoroi-create-wallet.webp" alt="Yoroi créer un portefeuille" />

        <p>Sélectionnez "se connecter à un hardware wallet". Yoroi va vous demander à quelle plateforme vous voulez vous connecter.</p>

        <img v-lazy-load data-src="/img/tuto-ledger-yoroi/yoroi-choose-platform.webp" alt="Yoroi choisir plateforme" />

        <p>Choisissez Cardano. Yoroi vous demande alors quel hardware wallet vous possédez.</p>

        <img v-lazy-load data-src="/img/tuto-ledger-yoroi/yoroi-choose-hardware-wallet.webp" alt="Yoroi choisir COLD wallet" />

        <p>Sélectionnez votre portefeuille. Enfin, Yoroi va vous demander dans quelle ère vous voulez créer votre portefeuille</p>

        <img v-lazy-load data-src="/img/tuto-ledger-yoroi/yoroi-choose-era.webp" alt="Yoroi choisir l'ère de Cardano" />

        <p>Choisissez l'ère actuelle de Cardano: <b>Shelley</b></p>

        <img v-lazy-load data-src="/img/tuto-ledger-yoroi/yoroi-start-hardware-wallet-connection.webp" alt="Yoroi démarrer la connexion au hardware wallet" />

        <p>
            La connexion à votre COLD wallet va enfin commencer, cliquez sur suivant une fois que vous avez validé les prérequis.
            Si vous utilisez un <b>Ledger</b>, Yoroi vous demandera lequel vous utilisez : S ou X.
        </p>

        <img v-lazy-load data-src="/img/tuto-ledger-yoroi/choose-device.webp" alt="Yoroi choisir quel ledger" />

        <p>
            A partir de là, votre Ledger va s'activer. Yoroi va vous demander de confirmer l'export de la clé publique sur votre ledger.
            A chaque étape, il vous suffira d'appuyer sur les 2 boutons en même temps pour valider.
        </p>

        <img v-lazy-load data-src="/img/tuto-ledger-yoroi/ledger-export-public-key.webp" alt="Ledger exporter la clé publique" />
        <img v-lazy-load data-src="/img/tuto-ledger-yoroi/ledger-export-public-key-confirm.webp" alt="Ledger: confirmation d'exportation de la clé publique" />

        <img v-lazy-load data-src="/img/tuto-ledger-yoroi/choose-wallet-name.webp" alt="Yoroi choisir le nom du portefeuille" />

        <p>
            Choisissez un nom pour votre portefeuille (qui vous rappellera que celui-ci est bien connecté à votre cold wallet).
        </p>

        <img v-lazy-load data-src="/img/tuto-ledger-yoroi/yoroi-connected.webp" alt="Yoroi portefeuille créé" />

        <p>
            Ca y est, votre portefeuille connecté à votre Ledger est créé !
        </p>

        <h2>Virer des fonds vers votre portefeuille</h2>
        <p>
            Afin de commencer la délégation de notre nouveau portefeuille tout frais, nous allons y transférer des fonds.
            Nous devons commencer par là, car la délégation nécessite un <b>dépôt de 2 ADAs</b> (que nous récupérerons lorsqu'on
            arrêtera la délégation), ainsi que l'acquittement de <b>frais de transactions</b> pour déposer notre délégation
            sur la blockchain. Au moment ou j'écris ce tutoriel, les frais de transaction sont à moins de <b>0.2 ADAs</b> soit environ
            2 centimes. On va pas se ruiner.
        </p>

        <p>Allez dans l'onglet "Receive" de Yoroi pour y trouver une adresse de votre portefeuille.</p>

        <img v-lazy-load data-src="/img/tuto-ledger-yoroi/yoroi-receive.webp" alt="Adresse Yoroi" />

        <p>
            Â cette adresse, vous pouvez envoyer vos fonds depuis vos autres portefeuilles ou votre exchange préféré.
            Si l'idée de faire des transactions en cryptomonnaies vous inquiète un peu, n'hésitez pas à commencer par une
            somme plus petite. Au prix des frais de transaction de Cardano (quelques centimes), on peut largement se le permettre.
        </p>

        <p>
            Exemple d'envoi depuis Binance:
        </p>

        <img v-lazy-load data-src="/img/tuto-daedalus/envoyer-ada-depuis-binance.webp" alt="Envoyer des ADAs depuis Binance" />

        <p>Moins d'une dizaine de minutes plus tard, vous verrez la transaction apparaître sur Yoroi. Pour ce tutoriel, j'ai envoyé 108 ADAs.</p>

        <img v-lazy-load data-src="/img/tuto-ledger-yoroi/yoroi-transaction.webp" alt="Yoroi transaction arrivée" />

        <p>
            10 minutes, ça peut sembler long, mais n'oubliez pas que votre banque aurait mis 2 jours ou plus.
            Ca y est, nous sommes fin prêt pour le staking !
        </p>

        <h2>Déléguer le portefeuille à une Stake Pool</h2>
        <p>
            Notre portefeuille est créé et approvisionné, il ne lui manque plus que les intérêts. Cliquez sur l'onglet <mark>Délégation (liste)</mark>.
            Vous verrez apparaître une liste de <a href="/doc-cardano/qu-est-ce-qu-une-stake-pool">Stake Pools</a> ou <i>Staking Pools</i>.
        </p>

        <img v-lazy-load data-src="/img/tuto-ledger-yoroi/yoroi-delegation-tab.webp" alt="Yoroi onglet délégation" />

        <p>
            Choisissez une Stake Pool de cette liste. <b>Si vous ne savez pas comment choisir la meilleure Stake Pool pour vous, n'hésitez pas à
                lire notre article</b>:
            <a href="/tutoriel/comment-choisir-une-stake-pool-pour-deleguer-ses-ADAs"><b><u>Comment choisir une Stake Pool</u></b></a>.
            Dans ce tutoriel, nous allons déleguer à SPFR, la Stake Pool de Stakepool.fr <i class="far fa-meh-rolling-eyes"></i>
            Une fois votre Stake Pool choisie, cliquez sur <span class="button">Déleguer</span>.
        </p>

        <p>
            A ce stade, Yoroi va vous demander de préparer votre Ledger: vous aurez besoin de valider la transaction dessus.
        </p>

        <img v-lazy-load data-src="/img/tuto-ledger-yoroi/yoroi-delegate-ledger.webp" alt="Yoroi délégation ledger" />

        <p>
            Suivez simplement les instructions de Yoroi. Sur votre Ledger, validez les différentes étapes affichées en appuyant sur les
            2 boutons simultanément. Le ledger vous montrera les différentes informations de la transaction, comme le TTL ou les frais de
            transactions. Voici quelques unes des étapes:
        </p>

        <img v-lazy-load data-src="/img/tuto-ledger-yoroi/ledger-register-staking-key.webp" alt="Ledger enregistrement de la clé d'enjeu" />
        <img v-lazy-load data-src="/img/tuto-ledger-yoroi/ledger-register-staking-key-show.webp" alt="Ledger montre la clé d'enjeu" />
        <img v-lazy-load data-src="/img/tuto-ledger-yoroi/ledger-register-staking-key-confirm.webp" alt="Ledger confirmation d'enregistrement de la clé d'enjeu" />
        <img v-lazy-load data-src="/img/tuto-ledger-yoroi/ledger-pool-key.webp" alt="Ledger affichage de la clé du groupe d'enjeu" />
        <img v-lazy-load data-src="/img/tuto-ledger-yoroi/ledger-confirm-delegation.webp" alt="Ledger affichage de la clé du groupe d'enjeu" />

        <p>
            Une fois que vous avez tout validé, Yoroi vous affichera une popup de confirmation. D'ici quelques minutes, votre transaction
            sera enregistrée sur la blockchain. Félicitation, vous participez désormais
            à valider les transactions du réseau, et gagnerez des récompenses pour ça ! Vous pouvez voir votre délégation dans
            l'onglet Tableau de bord.
        </p>

        <img v-lazy-load data-src="/img/tuto-ledger-yoroi/yoroi-delegate-success.webp" alt="Yoroi délégation ledger" />

        <p>
            La première récompense vous sera payé après 3 <b>époques</b> (de 5 jours chacune). Par la suite, elles vont seront versées à la fin
            de chaque époque. Bon Staking à tous !
        </p>
    </div>
</template>

<script>
import createHeader from "../../js/meta.js";

export default {
    layout: "DocLayout",
    head: createHeader({
        title:
            "Comment déléguer ses ADAs (contre des intérêts) avec Yoroi - Stakepool.fr",
        description:
            "Yoroi est un portefeuille HOT ou COLD (compatible Trezor et Ledger Nano X ou S) intégré à votre navigateur, qui vous permet de déleguer vos ADAs et de gagner des intérêts dessus."
    })
};
</script>